// ---- Größenfinder Allgemein: inline size recommendation funnel ----
//
// Same scoring logic as assets/size-finder.js (product size finder), but rendered inline on a
// page instead of a modal, and without any product/variant/cart integration: the last step links
// to a collection instead of adding a recommended variant to the cart.
//
// Wrapped in an IIFE because this is loaded as a classic (non-module) script alongside
// size-finder.js: top-level const/class declarations share one global lexical scope across
// <script> tags, so without this wrapper the duplicate names (SIZE_TABLES, scoreSizes, etc.)
// throw "Identifier has already been declared" and silently break both scripts.
(function () {
const SIZE_TABLES = {
  // Oberteile (inkl. Hemden)
  tops: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 55, weightMax: 68 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 63, weightMax: 75 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 70, weightMax: 85 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 80, weightMax: 95 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 90, weightMax: 105 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 100, weightMax: 115 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 110, weightMax: 130 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 120, weightMax: 145 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 135, weightMax: 160 }
  ],
  // Jacken & Mäntel
  outerwear: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 58, weightMax: 70 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 65, weightMax: 78 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 72, weightMax: 88 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 82, weightMax: 98 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 92, weightMax: 108 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 102, weightMax: 120 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 112, weightMax: 135 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 125, weightMax: 150 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 140, weightMax: 165 }
  ],
  // Unterteile
  bottoms: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 52, weightMax: 65 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 60, weightMax: 73 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 68, weightMax: 82 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 78, weightMax: 92 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 88, weightMax: 102 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 98, weightMax: 112 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 108, weightMax: 130 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 120, weightMax: 145 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 135, weightMax: 160 }
  ]
};

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

function distancesFromMeasurement(value, table, minKey, maxKey) {
  // A size counts as a direct match (distance 0) whenever the value falls inside its own
  // table range — no averaging/center comparison between overlapping ranges.
  const matches = table.reduce((acc, row, i) => {
    if (value >= row[minKey] && value <= row[maxKey]) acc.push(i);
    return acc;
  }, []);

  if (matches.length) {
    return table.map((row, i) => Math.min(...matches.map(m => Math.abs(i - m))));
  }

  if (value < table[0][minKey]) return table.map((row, i) => i);
  if (value > table[table.length - 1][maxKey]) return table.map((row, i) => table.length - 1 - i);

  let nearest = 0;
  let nearestDist = Infinity;
  table.forEach((row, i) => {
    const dist = value < row[minKey] ? row[minKey] - value : value - row[maxKey];
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = i;
    }
  });
  return table.map((row, i) => Math.abs(i - nearest));
}

function pointsForDistance(dist, exact, oneOff, twoOff, threeOrMoreOff) {
  if (dist === 0) return exact;
  if (dist === 1) return oneOff;
  if (dist === 2) return twoOff;
  return threeOrMoreOff;
}

function scoreSizes(state) {
  const table = SIZE_TABLES[state.table];
  const heightDistances = distancesFromMeasurement(state.height, table, 'heightMin', 'heightMax');
  const weightDistances = distancesFromMeasurement(state.weight, table, 'weightMin', 'weightMax');
  const commonIdx = SIZE_ORDER.indexOf(state.commonSize);

  const baseScores = table.map((row, i) => {
    const commonDist = commonIdx === -1 ? null : Math.abs(i - commonIdx);

    let score = pointsForDistance(heightDistances[i], 6, 2, 1, 0) + pointsForDistance(weightDistances[i], 6, 2, 1, 0);
    if (commonDist !== null) score += pointsForDistance(commonDist, 8, 4, 0, 0);
    return score;
  });

  let leader = 0;
  baseScores.forEach((value, i) => {
    if (value > baseScores[leader]) leader = i;
  });

  let favored = leader;
  let penalized = null;
  if (state.fit === 'tight') {
    favored = leader - 1;
    penalized = leader + 1;
  } else if (state.fit === 'loose') {
    favored = leader + 1;
    penalized = leader - 1;
  }

  const finalScores = baseScores.map((value, i) => {
    if (i === favored) return value + 2;
    if (i === penalized) return value - 2;
    return value;
  });

  return table
    .map((row, i) => ({
      size: row.size,
      points: finalScores[i],
      index: i,
      percent: Math.max(0, Math.min(100, Math.round((finalScores[i] * 100) / 22)))
    }))
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      // Tie-break: the size favored by the fit preference (question 3) wins
      if (a.index === favored) return -1;
      if (b.index === favored) return 1;
      return 0;
    });
}

// ---- Wizard controller ----

class SizeFinderGeneralInstance {
  constructor(root) {
    this.root = root;
    this.state = this.initialState();

    this.backBtn = root.querySelector('[data-sfg-back]');
    this.nextBtn = root.querySelector('[data-sfg-next]');
    this.ctaLink = root.querySelector('[data-sfg-cta]');
    this.ctaUrls = {};
    this.ctaLabels = {};
    try {
      this.ctaUrls = JSON.parse(this.ctaLink?.dataset.ctaUrls || '{}');
    } catch (e) {
      this.ctaUrls = {};
    }
    try {
      this.ctaLabels = JSON.parse(this.ctaLink?.dataset.ctaLabels || '{}');
    } catch (e) {
      this.ctaLabels = {};
    }

    this.bindEvents();
    this.reset();
  }

  initialState() {
    return { step: 1, category: null, categoryLabel: '', table: null, height: null, weight: null, commonSize: null, fit: null };
  }

  bindEvents() {
    this.root.querySelectorAll('[data-category]').forEach(btn => {
      btn.addEventListener('click', () => this.selectCategory(btn));
    });

    this.root.querySelectorAll('[data-common-size]').forEach(btn => {
      btn.addEventListener('click', () => this.selectCommonSize(btn));
    });

    this.root.querySelectorAll('[data-fit]').forEach(btn => {
      btn.addEventListener('click', () => this.selectFit(btn));
    });

    const heightInput = this.root.querySelector('[data-field="height"]');
    const weightInput = this.root.querySelector('[data-field="weight"]');
    heightInput?.addEventListener('input', () => {
      this.state.height = heightInput.value ? parseFloat(heightInput.value) : null;
    });
    weightInput?.addEventListener('input', () => {
      this.state.weight = weightInput.value ? parseFloat(weightInput.value) : null;
    });

    this.backBtn?.addEventListener('click', () => this.goBack());
    this.nextBtn?.addEventListener('click', () => this.goNext());
  }

  selectCategory(btn) {
    this.root.querySelectorAll('[data-category]').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    this.state.category = btn.dataset.category;
    this.state.table = btn.dataset.table;
    this.state.categoryLabel = btn.dataset.label;
  }

  selectCommonSize(btn) {
    this.root.querySelectorAll('[data-common-size]').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    this.state.commonSize = btn.dataset.commonSize;
  }

  selectFit(btn) {
    this.root.querySelectorAll('[data-fit]').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    this.state.fit = btn.dataset.fit;
  }

  reset() {
    this.state = this.initialState();
    this.result = null;

    // Preselect the first option of steps 1, 3 and 4, matching the design. Calling the select
    // methods directly (instead of .click()) avoids moving real browser focus onto these buttons
    // on page load, which would otherwise hijack the page's initial tab order.
    const firstCategory = this.root.querySelector('[data-category]');
    const firstCommonSize = this.root.querySelector('[data-common-size]');
    const firstFit = this.root.querySelector('[data-fit]');
    if (firstCategory) this.selectCategory(firstCategory);
    if (firstCommonSize) this.selectCommonSize(firstCommonSize);
    if (firstFit) this.selectFit(firstFit);

    const heightInput = this.root.querySelector('[data-field="height"]');
    const weightInput = this.root.querySelector('[data-field="weight"]');
    if (heightInput) heightInput.value = '';
    if (weightInput) weightInput.value = '';
    this.state.height = null;
    this.state.weight = null;

    this.root.querySelectorAll('[data-step-error]').forEach(el => { el.hidden = true; });

    // No focusTitle here: this initial render must not steal keyboard focus from the rest of
    // the page (e.g. the site navigation) before the visitor has interacted with the wizard.
    this.showStep(1, { focusTitle: false });
  }

  showStep(step, { focusTitle = true } = {}) {
    this.state.step = step;

    this.root.querySelectorAll('[data-step]').forEach(el => {
      el.hidden = Number(el.dataset.step) !== step;
    });

    this.root.querySelectorAll('[data-dot]').forEach(dot => {
      dot.classList.toggle('is-active', Number(dot.dataset.dot) === step);
    });

    if (this.backBtn) this.backBtn.hidden = step === 1;
    if (this.nextBtn) this.nextBtn.hidden = step === 5;
    if (this.ctaLink) this.ctaLink.hidden = step !== 5;

    if (step === 5) {
      this.renderResult();
      this.updateCtaLink();
    }

    this.updateStepTitle(step);

    if (focusTitle) {
      const titleEl = this.root.querySelector(`[data-step="${step}"] .sfg-step__title`);
      titleEl?.focus({ preventScroll: true });
    }
  }

  updateStepTitle(step) {
    if (step !== 3 && step !== 5) return;
    const titleEl = this.root.querySelector(`[data-step="${step}"] [data-template]`);
    if (!titleEl) return;
    titleEl.textContent = (titleEl.dataset.template || '').replace('%CATEGORY%', this.state.categoryLabel || '');
  }

  validateStep(step) {
    if (step === 1) return !!this.state.category;

    if (step === 2) {
      const valid = this.state.height >= 100 && this.state.height <= 230 && this.state.weight >= 30 && this.state.weight <= 250;
      const errorEl = this.root.querySelector('[data-step="2"] [data-step-error]');
      if (errorEl) errorEl.hidden = valid;
      return valid;
    }

    if (step === 3) return !!this.state.commonSize;
    if (step === 4) return !!this.state.fit;

    return true;
  }

  goNext() {
    if (!this.validateStep(this.state.step)) return;
    if (this.state.step < 5) this.showStep(this.state.step + 1);
  }

  goBack() {
    if (this.state.step > 1) this.showStep(this.state.step - 1);
  }

  updateCtaLink() {
    if (!this.ctaLink) return;
    const url = this.ctaUrls[this.state.category];
    if (url) this.ctaLink.href = url;
    const label = this.ctaLabels[this.state.category];
    if (label) this.ctaLink.textContent = label;
  }

  renderResult() {
    this.result = scoreSizes(this.state).slice(0, 2);
    const container = this.root.querySelector('[data-result-container]');
    const i18n = window.sizeFinderGeneralI18n || {};

    if (!container) return;

    container.innerHTML = this.result
      .map((entry, i) => {
        const percentText = (i18n.matchPercent || 'Zu %PERCENT%% wird dir dieser Artikel in Größe %SIZE% passen')
          .replace('%PERCENT%', entry.percent)
          .replace('%SIZE%', entry.size);

        return `
          <div class="sfg-result__row${i === 0 ? ' sfg-result__row--primary' : ''}">
            <div class="sfg-result__row-header">
              <span class="sfg-result__size">${i18n.sizeLabel ? i18n.sizeLabel + ' ' : 'Größe '}${entry.size}</span>
              ${i === 0 ? `<span class="badge">${i18n.recommendationBadge || 'Empfehlung'}</span>` : ''}
            </div>
            <div class="sfg-result__bar-row">
              <div class="sfg-result__bar"><div class="sfg-result__bar-fill" style="width:${entry.percent}%"></div></div>
              <span class="sfg-result__percent">${entry.percent}%</span>
            </div>
            <p class="sfg-result__percent-text text-sm text-subdued">${percentText}</p>
          </div>`;
      })
      .join('');
  }
}

function initSizeFinderGeneral() {
  document.querySelectorAll('[data-size-finder-general]').forEach(root => {
    if (root._sizeFinderGeneralInstance) return;
    root._sizeFinderGeneralInstance = new SizeFinderGeneralInstance(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeFinderGeneral);
} else {
  initSizeFinderGeneral();
}
})();
